import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Field } from './Field';
import { Input } from '@/components/ui/input';
import { TypePill } from './TypePill';
import { ComboboxListbox } from './ComboboxListbox';
import { useCombobox } from '@/hooks/useCombobox';
import { useDebounced } from '@/hooks/useDebounced';
import { SEARCH_MIN_CHARS, useItemsSearch } from '@/hooks/useInventory';
import { cn } from '@/lib/utils';
import type { Item } from '@/types/api';

/**
 * "Find the medicine you want to act on" — the field at the top of the Issue and
 * Adjust flows.
 *
 * The two pages had a local `ItemPicker` each: same name, same props, same
 * labels, same rows, same out-of-stock rule. They had drifted only in the ways
 * copies do — Issue closed its menu when you clicked away and Adjust did not,
 * and neither had keyboard navigation. There was never a second behaviour to
 * justify a second component.
 *
 * Searches the clinic's own stock, not the medicine reference catalogue: you can
 * only issue or adjust something you hold.
 */
interface ItemSearchFieldProps {
  onPick: (item: Item) => void;
  /**
   * An item that must not be picked again — the draft currently being edited.
   * `null` when there isn't one.
   */
  disabledItemId: string | null;
}

export function ItemSearchField({ onPick, disabledItemId }: ItemSearchFieldProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const debounced = useDebounced(query.trim(), 200);

  const { data: results = [], isFetching } = useItemsSearch({
    query: debounced,
    limit: 8,
    enabled: debounced.length >= SEARCH_MIN_CHARS,
  });

  const combobox = useCombobox<Item>({
    text: query,
    onTextChange: setQuery,
    options: results,
    optionKey: (item) => item.id,
    // Out of stock is not something you can issue, and the draft already open
    // would be picked twice. Both stay in the list, greyed: "it isn't here" and
    // "it's here and you can't have it" are different answers, and hiding the
    // row gives the wrong one.
    isOptionDisabled: (item) => item.totalStock <= 0 || item.id === disabledItemId,
    onSelect: (item) => {
      onPick(item);
      setQuery('');
    },
    minChars: SEARCH_MIN_CHARS,
    showsEmptyState: true,
    ariaLabel: t('issue.findItem'),
  });

  return (
    <Field label={t('issue.findItem')} hint={t('issue.findItemHint')}>
      <div ref={combobox.rootRef}>
        <div ref={combobox.anchorRef}>
          <Input
            {...combobox.inputProps}
            placeholder={t('issue.placeholderSearch')}
            autoComplete="off"
          />
        </div>

        <ComboboxListbox
          {...combobox.listboxProps}
          emptyState={isFetching ? t('common.searching') : t('issue.noItemsMatch')}
          renderOption={(item) => {
            const outOfStock = item.totalStock <= 0;
            return (
              <>
                <div className="min-w-0 flex-1">
                  <div className="text-[13.5px] font-semibold tracking-[-0.01em]">
                    {item.brandName}
                  </div>
                  <div className="truncate text-[12px] text-muted">
                    {item.genericName}
                    {item.strength ? ` · ${item.strength}` : ''}
                  </div>
                </div>
                <TypePill type={item.type} size="sm" />
                <span
                  className={cn(
                    'ml-2 font-mono text-[12px] tabular-nums',
                    outOfStock ? 'text-destructive' : 'text-muted',
                  )}
                >
                  {outOfStock
                    ? t('common.outOfStock')
                    : t('common.inStockN', { count: item.totalStock })}
                </span>
              </>
            );
          }}
        />
      </div>
    </Field>
  );
}
